import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';
import { Conversation } from "@42maru/webchat";
import { LabelKeyValueItem, MessageHistorySearchType } from '@/model';
import { UTCToLocalString } from '@/util/dateUtil';
import { usePaginatedConversations } from '@/query/conversation';
import { usePageParam, useScenarioId } from '@/hook';
import { successToast } from '@/component/Toast';
import { TypeSearchbar } from '@/component/input';
import { DateRangePicker, DateRange } from '@/component/dateRangePicker';
import TextBadge from '@/component/TextBadge';
import { InfoIcon, PasteIcon } from '@/assets/svgs/icons';
import SpeechBubbleIcon from '@/assets/svgs/speech_bubble2.svg';

import ConversationMessageList from './ConversationMessageList';
 

const CONVERSATION_SIZE = 20;

const searchTypeToOption: LabelKeyValueItem<MessageHistorySearchType>[] = [
    { labelKey: "i07005", value: "user_query" },
    { labelKey: "i07006", value: "bot_query" },
    { labelKey: "i07004", value: "conversation_id" },
    { labelKey: "i07010", value: "user_key" },
];

type ConversationHistoryPageParam = {
    page: number;
    size: number;
    searchType: MessageHistorySearchType;
    searchKeyword: string;
}

interface ConversationHistoryTabProps {
    period: DateRange;
    setPeriod: (period: DateRange) => void;
}

const ConversationHistoryTab: React.FC<ConversationHistoryTabProps> = (props) => {
    const { period, setPeriod } = props;

    const intl = useIntl();
    const scenarioId = useScenarioId();
    const id = useId();

    const cardListRef = useRef<HTMLDivElement>(null);
    const [selectedConversation, setSelectedConversation] = useState<Conversation>();
    const userKey = useMemo(() => selectedConversation?.last_message?.context?.system?.global?.user_key ?? "", [selectedConversation]);

    const { param, setParam, updateCallback } = usePageParam<ConversationHistoryPageParam>({
        page: 0,
        size: CONVERSATION_SIZE,
        searchType: "user_key",
        searchKeyword: ""
    });

    const {
        data: conversationPages,
        hasNextPage,
        fetchNextPage
    } = usePaginatedConversations({
        scenarioId: scenarioId,
        startDate: period.start!.format("YYYY-MM-DD HH:mm:ss"),
        endDate: period.end!.format("YYYY-MM-DD HH:mm:ss"),
        searchType: param.searchType,
        searchKeyword: param.searchKeyword,
        size: param.size
    });

    const onClickCopySessionKey = () => {
        navigator.clipboard.writeText(selectedConversation?.id ?? "")
            .then(() => successToast(intl.formatMessage({ id: "i07013" })));
    };
    const onClickCopyUserKey = () => {
        navigator.clipboard.writeText(userKey)
            .then(() => successToast(intl.formatMessage({ id: "i07014" })));
    };

    const onCardListKeyDown = (e: React.KeyboardEvent) => {
        const options = cardListRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
        if (!options?.length) { return; }
        const current = Array.from(options).indexOf(e.target as HTMLElement);
        if (current === -1) { return; }

        let next = current;
        if (e.key === "ArrowDown") { next = Math.min(current + 1, options.length - 1); }
        else if (e.key === "ArrowUp") { next = Math.max(current - 1, 0); }
        else { return; }

        e.preventDefault();
        options[next].focus();
    };

    const onScrollToCardListBottom = () => {
        if (hasNextPage) {
            fetchNextPage().then();
        }
    };

    const getMessagecount = useCallback((conversation: Conversation) => {
        return conversation.last_message?.context?.system?.global?.response_count || 0;
    }, []);

    const conversations = useMemo(() => (
        conversationPages?.pages.flatMap(page => page.content)
    ), [conversationPages]);

    useEffect(() => {    
        const scrollToTop = () => {
            cardListRef.current?.scrollTo({
                top: 0,
            });
        };
        updateCallback(scrollToTop);
    }, [updateCallback]);

    useEffect(() => {
        if (!conversations || !selectedConversation) {
            return;
        }
        if (!conversations.some(conversation => conversation.id === selectedConversation.id)) {
            setSelectedConversation(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations]);

    return (
        <div className="w-full min-w-fit flex-1 min-h-0 flex flex-col gap-3"
        >
            <div className="flex-none flex gap-1.5">
                <DateRangePicker useTime
                                 period={period}
                                 onChangePeriod={setPeriod}
                />

                <TypeSearchbar className="w-[680px]"
                               searchTypes={searchTypeToOption}
                               placeholder={intl.formatMessage({ id: "i07003" })}
                               onSearch={(keyword, option) => {
                                   setParam({
                                       ...param,
                                       searchType: option.value as MessageHistorySearchType,
                                       searchKeyword: keyword
                                   });
                               }}
                />
            </div>

            <div className="flex-1 w-full min-h-0 flex gap-1 overflow-hidden">
                <div className="w-[444px] pr-1.5 overflow-y-auto">
                    <div ref={cardListRef}
                         role="listbox"
                         aria-label={intl.formatMessage({ id: "i07002" })}
                         aria-orientation="vertical"
                         className="flex-1 min-h-0 max-h-fit flex flex-col gap-1 overflow-y-auto"
                         onKeyDown={onCardListKeyDown}
                    >
                        {conversations?.map(conversation => (
                            <button key={conversation.id}
                                    id={`conversation-list-item-${conversation.id}`}
                                    role="option"
                                    aria-selected={selectedConversation?.id === conversation.id}
                                    aria-label={`${intl.formatMessage({ id: "i09007" })} ${(conversation.id ?? "-").slice(0, 8)}`}
                                    className={classNames(
                                        "py-2.5 px-4 w-full flex flex-col items-start gap-1.5 rounded-[6px] border",
                                        "bg-white border-black-200 hover:bg-black-100 text-black-500",
                                        "aria-selected:bg-chat42-10 aria-selected:border-chat42 aria-selected:text-chat42",
                                    )}
                                    onClick={() => setSelectedConversation(conversation)}
                            >
                                <div className="w-full h-6 flex items-center justify-between">
                                    <div className={classNames(
                                            "w-[126px] text-[14px] tabular-nums truncate",
                                            selectedConversation?.id === conversation.id ? "font-[600]" : "font-[400]"
                                        )}
                                    >
                                        {conversation.id ?? "-"}
                                    </div>
                                    {conversation.last_message?.created_at
                                        ? <time dateTime={conversation.last_message.created_at}
                                                 className="text-[12px] font-[400]"
                                          >
                                              {UTCToLocalString(conversation.last_message.created_at, "YYYY-MM-DD HH:mm:ss")}
                                          </time>
                                        : <span className="text-[12px] font-[400]">-</span>
                                    }
                                </div>
                                <div className="w-full flex items-center justify-between">
                                    <TextBadge className="w-[256px] px-2 py-[3px] flex items-center rounded-[6px] text-[14px] font-[500]"
                                               text={conversation.last_message?.scene_name ?? ""}
                                    />
                                    <div aria-label={`${intl.formatMessage({ id: "b0056" })} ${getMessagecount(conversation)}`}
                                         className="flex items-center gap-1.5"
                                    >
                                        <img src={SpeechBubbleIcon} alt=""
                                             aria-hidden="true"
                                             className="size-4"
                                        />
                                        <div className="text-[12px] font-[400] tabular-nums">
                                            {getMessagecount(conversation)}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    
                        {hasNextPage && (
                            <InView as="div"
                                    aria-hidden="true"
                                    className="h-px"
                                    onChange={inView => inView && onScrollToCardListBottom()}
                            />
                        )}
                    </div>
                </div>

                {!selectedConversation && (
                    <div id="conversation-detail"
                         role="region"
                         aria-label={intl.formatMessage({ id: "i07002" })}
                         className="w-[700px] flex items-center justify-center text-[14px] text-black-400"
                    >
                        {intl.formatMessage({ id: "i07016" })}
                    </div>
                )}
                {selectedConversation && (
                    <div id="conversation-detail"
                         role="region"
                         aria-labelledby={`conversation-list-item-${selectedConversation.id}`}
                         className="w-[700px] flex flex-col gap-1"
                    >
                        <div className="px-6 py-3.5 flex flex-col justify-center gap-2 rounded-[6px] bg-black-200 border border-black-200">
                            <div className="flex items-center gap-2 text-[14px] font-[400] text-black-500">
                                <span id={`${id}-session`}>
                                    {`${intl.formatMessage({ id: "i07004" })} : ${selectedConversation?.id ?? "-"}`}
                                </span>
                                <button aria-labelledby={`${id}-session ${id}-session-copy`}
                                        onClick={onClickCopySessionKey}
                                >
                                    <PasteIcon aria-hidden="true"
                                               className="size-4"
                                    />
                                    <span id={`${id}-session-copy`}
                                          className="sr-only"
                                    >
                                        {intl.formatMessage({ id: "b0062" })}
                                    </span>
                                </button>
                            </div>
                            <div className="flex items-end justify-between">
                                <div className="flex items-center gap-2 text-[14px] font-[400] text-black-500">
                                    <span id={`${id}-user`}>
                                        {`${intl.formatMessage({ id: "i07010" })} : ${userKey || "-"}`}
                                    </span>
                                    <button aria-labelledby={`${id}-user ${id}-user-copy`}
                                            onClick={onClickCopyUserKey}
                                    >
                                        <PasteIcon aria-hidden="true"
                                                   className="size-4"
                                        />
                                        <span id={`${id}-user-copy`}
                                              className="sr-only"
                                        >
                                            {intl.formatMessage({ id: "b0062" })}
                                        </span>
                                    </button>
                                </div>
                                <div className="ml-auto flex items-center gap-1">
                                    <InfoIcon aria-hidden="true"
                                              className="size-3.5 text-black-400"
                                    />
                                    <div className="text-[12px] font-[400] text-black-500">
                                        {intl.formatMessage({ id: "i07015" })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ConversationMessageList conversation={selectedConversation}
                                                 searchType={param.searchType}
                                                 searchKeyword={param.searchKeyword}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConversationHistoryTab;
